import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Plus } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { Genre, NarrativeForm, logger } from '@musedrops/shared';
import {
  getGenres,
  createGenre,
  updateGenre,
  deleteGenre,
} from '../../../data/genre';
import { listAllNarrativeForms } from '../../../data/narrativeForms';
import { TagsInput } from '../../../shared/components/TagsInput';

interface GenreWithUsage extends Genre {
  showCount: number;
}

export const GenresSettings: React.FC = () => {
  const notify = useNotify();
  const [genres, setGenres] = useState<GenreWithUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Narrative forms, loaded once, for the per-genre "Forms" coverage count.
  const [forms, setForms] = useState<NarrativeForm[]>([]);

  // Dialog state - shared for create and edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingGenre, setEditingGenre] = useState<GenreWithUsage | null>(null);
  const [dialogName, setDialogName] = useState('');
  const [dialogDescription, setDialogDescription] = useState('');
  const [dialogInstructions, setDialogInstructions] = useState('');
  const [dialogTags, setDialogTags] = useState<string | null>('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingGenre, setDeletingGenre] = useState<GenreWithUsage | null>(
    null
  );

  useEffect(() => {
    loadGenres();
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setForms(await listAllNarrativeForms());
    } catch (err) {
      // Non-fatal: the Forms coverage column just shows 0 if this fails.
      logger.error('Failed to load narrative forms for coverage counts', {
        error: err,
      });
    }
  };

  // A form counts for a genre if it has no compatible_genres restriction
  // (null/empty = "all genres") or explicitly includes that genre's name.
  const formsCountForGenre = (genreName: string): number =>
    forms.filter(
      form =>
        !form.compatibleGenres ||
        form.compatibleGenres.length === 0 ||
        form.compatibleGenres.includes(genreName)
    ).length;

  const loadGenres = async () => {
    try {
      setLoading(true);
      setError(null);

      const allGenres = await getGenres();

      // Map genres to include showCount (already in the data from DB)
      const genresWithUsage: GenreWithUsage[] = allGenres.map(genre => ({
        ...genre,
        showCount: genre.showCount ?? 0,
      }));

      setGenres(genresWithUsage);
    } catch (err) {
      logger.error('Failed to load genres:', err);
      setError(err instanceof Error ? err.message : 'Failed to load genres');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setDialogMode('create');
    setEditingGenre(null);
    setDialogName('');
    setDialogDescription('');
    setDialogInstructions('');
    setDialogTags('');
    setDialogOpen(true);
  };

  const handleEditClick = (genre: GenreWithUsage) => {
    setDialogMode('edit');
    setEditingGenre(genre);
    setDialogName(genre.name);
    setDialogDescription(genre.description);
    setDialogInstructions(genre.instructions);
    setDialogTags(genre.tags || '');
    setDialogOpen(true);
  };

  const handleDialogSave = async () => {
    if (dialogMode === 'create') {
      try {
        await createGenre({
          name: dialogName,
          description: dialogDescription,
          instructions: dialogInstructions,
          tags: dialogTags || null,
        });
        notify('Genre created successfully', { type: 'success' });
        setDialogOpen(false);
        setDialogName('');
        setDialogDescription('');
        setDialogInstructions('');
        setDialogTags('');
        loadGenres();
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Failed to create genre', {
          type: 'error',
        });
      }
    } else {
      if (!editingGenre) return;

      try {
        await updateGenre(editingGenre.id, {
          name: dialogName,
          description: dialogDescription,
          instructions: dialogInstructions,
          tags: dialogTags || null,
        });
        notify('Genre updated successfully', { type: 'success' });
        setDialogOpen(false);
        setEditingGenre(null);
        setDialogName('');
        setDialogDescription('');
        setDialogInstructions('');
        setDialogTags('');
        loadGenres();
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Failed to update genre', {
          type: 'error',
        });
      }
    }
  };

  const handleDeleteClick = (genre: GenreWithUsage) => {
    setDeletingGenre(genre);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingGenre) return;

    try {
      await deleteGenre(deletingGenre.id);
      notify('Genre deleted successfully', { type: 'success' });
      setDeleteDialogOpen(false);
      setDeletingGenre(null);
      loadGenres();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to delete genre', {
        type: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Used By Shows</TableCell>
              <TableCell align="center">Forms</TableCell>
              <TableCell align="center">
                <IconButton
                  onClick={handleCreateClick}
                  color="primary"
                  size="small"
                >
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {genres.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No genres defined yet. Click + to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              genres.map(genre => (
                <TableRow
                  key={genre.id}
                  onClick={() => handleEditClick(genre)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Typography variant="body1">{genre.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {genre.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {genre.showCount > 0 ? (
                      <Typography variant="body2">{genre.showCount}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Link
                      component={RouterLink}
                      to={`/building-blocks/forms?genre=${encodeURIComponent(genre.name)}`}
                      onClick={e => e.stopPropagation()}
                      title={`View narrative forms compatible with ${genre.name}`}
                    >
                      {formsCountForGenre(genre.name)}
                    </Link>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClick(genre);
                      }}
                      disabled={genre.showCount > 0}
                      title={
                        genre.showCount > 0
                          ? `Cannot delete - used by ${genre.showCount} show(s)`
                          : 'Delete genre'
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'edit' ? 'Edit Genre' : 'Create Genre'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={dialogName}
            onChange={e => setDialogName(e.target.value)}
            helperText="Display name for the genre"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            value={dialogDescription}
            onChange={e => setDialogDescription(e.target.value)}
            helperText="Brief description of the genre"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Instructions"
            fullWidth
            multiline
            rows={4}
            value={dialogInstructions}
            onChange={e => setDialogInstructions(e.target.value)}
            helperText="Detailed AI instructions for generating content in this genre"
            required
            sx={{ mb: 2 }}
          />
          <TagsInput
            value={dialogTags}
            onChange={setDialogTags}
            label="Tags (Optional)"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDialogSave}
            variant="contained"
            disabled={!dialogName || !dialogDescription || !dialogInstructions}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Genre</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the genre{' '}
            <strong>{deletingGenre?.name}</strong>?
          </Typography>
          {deletingGenre && deletingGenre.showCount > 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This genre is used by {deletingGenre.showCount} show(s) and cannot
              be deleted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={!!deletingGenre && deletingGenre.showCount > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
